import {
  djangoHandleDataMutationRequest,
  handleSpringDataMutationRequest,
  handleSpringDataRequest,
} from "../../../api";
import {
  captureSignatureFromTopaz,
  DEFAULT_TOPAZ_PROMPT_TEXT,
  isTopazCaptureResetError,
  logTopazDebug,
  resetTopazCaptureSession,
} from "./topazSigPlusExtLite";

export const SIGNATURE_DEVICE_PROVIDERS = {
  NONE: "NONE",
  TOPAZ: "TOPAZ_SIGPLUSEXTLITE",
  SHIFT4: "SHIFT4_DEVICE",
  FORTIS: "FORTIS_DEVICE",
  DJANGO: "DJANGO_DEVICE",
};

export const DEFAULT_SIGNATURE_DEVICE_CONFIG = {
  configured: false,
  signature_device_name: SIGNATURE_DEVICE_PROVIDERS.NONE,
  device_config: {},
};

export { DEFAULT_TOPAZ_PROMPT_TEXT, logTopazDebug };
export { isTopazCaptureResetError };

export const fetchSignatureDeviceConfig = async (hotelId) => {
  if (!hotelId) {
    return DEFAULT_SIGNATURE_DEVICE_CONFIG;
  }

  try {
    const response = await handleSpringDataRequest(
      "core/api/v1/signature-device/get-config"
    );
    return {
      configured: response?.configured === true,
      signature_device_name:
        response?.signature_device_name || SIGNATURE_DEVICE_PROVIDERS.NONE,
      device_config: response?.device_config || {},
    };
  } catch (error) {
    console.error("Failed to fetch signature device config:", error);
    return DEFAULT_SIGNATURE_DEVICE_CONFIG;
  }
};

export const mergeSignatureDeviceConfig = (customPgData, signatureConfig) => ({
  ...(customPgData || {}),
  signature_device_name:
    signatureConfig?.signature_device_name || SIGNATURE_DEVICE_PROVIDERS.NONE,
  signature_device_config: signatureConfig?.device_config || {},
  signature_device_configured: signatureConfig?.configured === true,
});

const isLegacySignatureFlowVisible = (hotelCurrency) =>
  hotelCurrency === "$" || hotelCurrency === "USD";

export const resolveGuestSignatureFlow = ({
  customPgData,
  hotelCurrency,
}) => {
  const configured = customPgData?.signature_device_configured === true;
  const provider =
    customPgData?.signature_device_name || SIGNATURE_DEVICE_PROVIDERS.NONE;
  const deviceConfig = customPgData?.signature_device_config || {};
  const isEnabled = deviceConfig?.enabled !== false;

  if (configured) {
    return {
      configured,
      provider,
      visible: provider !== SIGNATURE_DEVICE_PROVIDERS.NONE && isEnabled,
      deviceConfig,
      isTopaz:
        provider === SIGNATURE_DEVICE_PROVIDERS.TOPAZ && isEnabled,
    };
  }

  const legacyVisible = isLegacySignatureFlowVisible(hotelCurrency);
  const legacyProvider =
    customPgData?.active_pg_name === "SHIFT4"
      ? SIGNATURE_DEVICE_PROVIDERS.SHIFT4
      : SIGNATURE_DEVICE_PROVIDERS.DJANGO;

  return {
    configured: false,
    provider: legacyVisible ? legacyProvider : SIGNATURE_DEVICE_PROVIDERS.NONE,
    visible: legacyVisible,
    deviceConfig: {},
    isTopaz: false,
  };
};

const getCurrentDateTime = () => {
  const now = new Date();
  const pad = (value) => value.toString().padStart(2, "0");
  const offset = -now.getTimezoneOffset();
  const sign = offset >= 0 ? "+" : "-";
  const offsetHours = pad(Math.floor(Math.abs(offset) / 60));
  const offsetMinutes = pad(Math.abs(offset) % 60);
  const milliseconds = now.getMilliseconds().toString().padStart(3, "0");

  return (
    `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}` +
    `T${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
      now.getSeconds()
    )}.${milliseconds}${sign}${offsetHours}:${offsetMinutes}`
  );
};

const requestShift4Signature = async ({
  customPgData,
  hotelId,
  bookingId,
  guestEmailId,
}) => {
  const shift4UtgUrl = customPgData?.shift4_utg_url;
  const shift4AccessToken = customPgData?.shift4_access_token;
  const shift4ApiTerminalId = customPgData?.shift4_api_terminal_id;

  if (!shift4UtgUrl || !shift4AccessToken || !shift4ApiTerminalId) {
    throw new Error("Shift4 terminal configuration is incomplete.");
  }

  const headers = new Headers();
  headers.append("InterfaceVersion", "4.0");
  headers.append("InterfaceName", "Stayflexi");
  headers.append("CompanyName", "Stayflexi");
  headers.append("AccessToken", shift4AccessToken);
  headers.append("Content-Type", "application/json");

  const requestBody = {
    dateTime: getCurrentDateTime(),
    device: {
      terminalId: shift4ApiTerminalId,
    },
  };

  const response = await fetch(
    `${shift4UtgUrl}api/rest/v1/devices/promptsignature`,
    {
      method: "POST",
      headers,
      body: JSON.stringify(requestBody),
      redirect: "follow",
    }
  );

  const json = await response.json();
  if (!response.ok) {
    throw new Error(json?.title || "Shift4 signature request failed.");
  }

  return handleSpringDataMutationRequest(
    "POST",
    `core/api/v1/payments/shift4/save-signature/?hotelId=${hotelId}&bookingId=${bookingId}&guestEmailId=${guestEmailId}`,
    json
  );
};

const requestLegacyDeviceSignature = ({
  hotelId,
  bookingId,
  guestEmailId,
  termsAndConditions,
  loggedUserEmail,
}) =>
  djangoHandleDataMutationRequest("POST", "invoice/sendTocSignatureRequest/", {
    hotelId,
    booking_id: bookingId,
    toc: termsAndConditions,
    cust_email: guestEmailId,
    ...(loggedUserEmail ? { logged_user_email: loggedUserEmail } : {}),
  });

export const captureTopazSignature = async ({
  flow,
  canvasElement,
  promptText,
}) => {
  if (flow?.provider !== SIGNATURE_DEVICE_PROVIDERS.TOPAZ) {
    throw new Error("Topaz signature capture is not enabled for this flow.");
  }

  const resolvedPromptText =
    promptText?.trim() ||
    flow?.deviceConfig?.prompt_text ||
    DEFAULT_TOPAZ_PROMPT_TEXT;

  logTopazDebug("ui.captureTopazSignature.start", {
    provider: flow?.provider,
    supportedModels: flow?.deviceConfig?.supported_models || [],
    promptTextLength: resolvedPromptText.length,
  });

  return captureSignatureFromTopaz({
    canvasElement,
    supportedModels: flow?.deviceConfig?.supported_models || [],
    promptText: resolvedPromptText,
  });
};

export const saveTopazSignature = async ({
  bookingId,
  guestEmailId,
  topazCapture,
}) => {
  if (!topazCapture?.signatureBase64) {
    throw new Error("Capture a Topaz signature before saving.");
  }

  logTopazDebug("ui.saveTopazSignature.start", {
    bookingId,
    guestEmailId,
    modelName: topazCapture.modelName,
    modelNumber: topazCapture.modelNumber,
    serialNumber: topazCapture.serialNumber,
    signatureBase64Length: topazCapture.signatureBase64?.length || 0,
    signatureStringLength: topazCapture.signatureString?.length || 0,
  });

  const response = await handleSpringDataMutationRequest(
    "POST",
    `core/api/v1/signature-device/save-topaz-signature?bookingId=${encodeURIComponent(
      bookingId
    )}`,
    {
      guest_email_id: guestEmailId,
      signature_base64: topazCapture.signatureBase64,
      topaz_model_name: topazCapture.modelName,
      topaz_model_number: topazCapture.modelNumber,
      topaz_serial_number: topazCapture.serialNumber,
      signature_string: topazCapture.signatureString,
      signature_prompt_text:
        topazCapture.promptText || DEFAULT_TOPAZ_PROMPT_TEXT,
    }
  );

  logTopazDebug("ui.saveTopazSignature.success", {
    bookingId,
    guestEmailId,
    responseStatus: response?.status || null,
    signedDocUrl: response?.signed_doc_url || null,
  });

  return {
    ...response,
    topazCapture,
  };
};

const requestTopazSignature = async ({
  bookingId,
  guestEmailId,
  flow,
  canvasElement,
  promptText,
}) => {
  const topazCapture = await captureTopazSignature({
    flow,
    canvasElement,
    promptText,
  });

  return saveTopazSignature({
    bookingId,
    guestEmailId,
    topazCapture,
  });
};

export const runGuestSignatureCapture = async ({
  flow,
  customPgData,
  hotelId,
  bookingId,
  guestEmailId,
  termsAndConditions,
  loggedUserEmail,
  topazCanvasElement,
  topazPromptText,
}) => {
  if (flow?.provider === SIGNATURE_DEVICE_PROVIDERS.TOPAZ) {
    return requestTopazSignature({
      bookingId,
      guestEmailId,
      flow,
      canvasElement: topazCanvasElement,
      promptText: topazPromptText,
    });
  }

  if (flow?.provider === SIGNATURE_DEVICE_PROVIDERS.SHIFT4) {
    return requestShift4Signature({
      customPgData,
      hotelId,
      bookingId,
      guestEmailId,
    });
  }

  return requestLegacyDeviceSignature({
    hotelId,
    bookingId,
    guestEmailId,
    termsAndConditions,
    loggedUserEmail,
  });
};

export const resetGuestSignatureCapture = async ({
  flow,
  topazCanvasElement,
}) => {
  if (flow?.provider === SIGNATURE_DEVICE_PROVIDERS.TOPAZ) {
    return resetTopazCaptureSession({
      canvasElement: topazCanvasElement,
    });
  }

  return false;
};

export const getGuestSignatureErrorMessage = (error, fallbackMessage) => {
  if (typeof error === "string" && error.trim()) {
    return error;
  }

  return (
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    fallbackMessage
  );
};
